import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import baseURL from '../baseURL';
import GirviPageShell from '../components/common/GirviPageShell';

const JournalRegister = () => {
  const [entries, setEntries] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB');
  };

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const profileRes = await fetch(baseURL + '/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const profile = await profileRes.json();
        setUser(profile.data?.user || null);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  // Fetch entries from backend
  const fetchEntries = async () => {
    setLoading(true);
    setError('');
    try {
      // Build query params
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      
      // Get authentication token
      const token = localStorage.getItem('token');
      
      // Get all accounting entries
      const res = await fetch(`${baseURL}/accounting-entries?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      
      // Filter for interest received (from receipts/releases), banking, and manual journal entries
      const filtered = data.data.filter(entry => {
        const desc = (entry.description || '').toLowerCase();
        const isInterest =
          (entry.source === 'receipt' || entry.source === 'release') &&
          desc.includes('interest');
        const isBanking = entry.source === 'banking' || desc.includes('bank');
        const isJournal = entry.entryType === 'journal';
        return isInterest || isBanking || isJournal;
      });
      setEntries(filtered);
    } catch (err) {
      setError('Failed to fetch journal entries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line
  }, []);

  // Totals
  const totalDebit = entries.reduce((sum, e) => sum + (e.transactionType === 'debit' ? e.amount : 0), 0);
  const totalCredit = entries.reduce((sum, e) => sum + (e.transactionType === 'credit' ? e.amount : 0), 0);

  // Helper to get document type display
  const getDocumentType = (entry) => {
    if (entry.entryType === 'journal') {
      return entry.entryNo || 'Journal Voucher';
    }
    if (entry.source === 'receipt' && entry.ticketNo) {
      return `Receipt - ${entry.ticketNo}`;
    }
    if (entry.source === 'release' && entry.ticketNo) {
      return `Release - ${entry.ticketNo}`;
    }
    if (entry.description?.toLowerCase().includes('interest')) {
      return 'Interest';
    }
    if (entry.documents && entry.documents[0]) {
      return entry.documents[0].type.toUpperCase();
    }
    return entry.source ? entry.source.toUpperCase() : '';
  };

  return (
    <GirviPageShell
      title="Journal Register"
      subtitle="Transactions · Registers"
      chipLabel="Transactions"
      maxWidth="xl"
     compact>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #e2e8f0' }}>
        <Grid container spacing={2} alignItems="center" justifyContent="center">
          {user && (
            <Grid item xs={12} sx={{ textAlign: 'center', mb: 1 }}>
              <Typography variant="h5" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                {user.companyName || user.name}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {user.address}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Mobile: {user.mobileNumber} | License: {user.licenseNumber}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Filter Section */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #e2e8f0' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="From Date"
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="To Date"
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={fetchEntries}
              disabled={loading}
            >
              Filter Entries
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label="journal register table">
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Document</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Account Name</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Details</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>Debit (₹)</TableCell>
                <TableCell align="right" sx={{ color: '#fff', fontWeight: 'bold' }}>Credit (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      No journal entries found for the selected period.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry, idx) => (
                  <TableRow
                    key={entry._id || idx}
                    sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }, '&:hover': { backgroundColor: '#f0f0f0' } }}
                  >
                    <TableCell>{formatDate(entry.date)}</TableCell>
                    <TableCell>{getDocumentType(entry)}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {entry.ledgerName || (entry.customerDetails && entry.customerDetails.name) || '-'}
                    </TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell align="right" sx={{ color: '#d32f2f', fontWeight: entry.transactionType === 'debit' ? 'bold' : 'normal' }}>
                      {entry.transactionType === 'debit' ? entry.amount.toFixed(2) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: entry.transactionType === 'credit' ? 'bold' : 'normal' }}>
                      {entry.transactionType === 'credit' ? entry.amount.toFixed(2) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {/* Total Row */}
              {entries.length > 0 && (
                <TableRow sx={{ backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                  <TableCell colSpan={4} align="right">
                    <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" fontWeight="bold" color="error">
                      {totalDebit.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" fontWeight="bold" color="success">
                      {totalCredit.toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </GirviPageShell>
  );
};

export default JournalRegister;
